import React, {useState, useEffect, useMemo, useCallback} from "react";
import RadarChart from "../reusableElementsComponents/RadarChart";
import TargetPositionComposition from "./TargetPositionComposition";
import CandidateCompetences from "./CandidateCompetences";
import CandidateMotivationFactors from "./CandidateMotivationFactors";
import ScoreLine from "../reusableElementsComponents/ScoreLine";
import ExplanationTooltip from "../reusableElementsComponents/ExplanationTooltip";
import {Accordion} from "react-bootstrap";
import 'react-circular-progressbar/dist/styles.css';
import {useSelector} from 'react-redux';
import { t, lookups } from '../../content';
import B2BApiService from "../../B2bApiService";
import {toast, ToastContainer} from "react-toastify";
import {getAONAssessmentLabelById, calculateAverageCeiling} from '../../Utilities'
import  * as Constants from '../../constants'
import downloadableReport from '../../assets/images/icons/downloadable-report.svg';

function TalentMatchScore({candidateData, matchPercentage}) {

    const [data, setData] = useState(null);

    const companyData = useSelector(state => state.company.info);

    // const copy = useSelector(state => state.contentfulData.all['talent-data']?.fields);
    const copy = {
        adeptAspectsLabels: lookups.adeptAspectLabels,
        adeptWorkStyles: lookups.adeptWorkStyleLabels,
        salesStrengthsLabels: lookups.salesStrengthLabels,
        teamStrengthsLabels: lookups.teamStrengthLabels,
        reasoningAndSkillsHelpLink: t('talents.help.reasoningAndSkills'),
        reasoningAndSkills: lookups.reasoningAndSkills,
        downloadableReportsSectionTitle: t('talents.sections.assessmentReports'),
        assessmentReportNames: lookups.reportNames,
        reportLanguages: lookups.reportLanguages,
    };

    const calculateWorkStyles = useCallback(() => {
        const combined = {};
        for (let i = 0; i < copy?.adeptAspectsLabels?.length; i++) {
            combined[copy?.adeptAspectsLabels[i]] = data?.adept_aspects_points[i];
        }

        return Object.keys(Constants.ADEPT_STYLES).map(category => {
            const traitNames = Constants.ADEPT_STYLES[category];
            const values = traitNames.map(trait => combined[trait] || 1); // Fetch scores or default to 0
            return calculateAverageCeiling(values);
        });
    }, [copy?.adeptAspectsLabels, data?.adept_aspects_points])

    useEffect(() => {
        if (candidateData) {
            setData(candidateData);
        }
    }, [candidateData]);

    useEffect(() => {

        setSalesChartData({
            labels: copy?.salesStrengthsLabels,
            datasets: [
                {
                    label: t('analytics.types.competence'),
                    backgroundColor: 'rgba(255,99,132,0.2)',
                    borderColor: 'rgba(255,99,132,1)',
                    pointBackgroundColor: 'rgba(179,181,198,1)',
                    pointHoverBorderColor: 'rgba(179,181,198,1)',
                    data: data?.sales_strengths?.competence
                },
                {
                    label: t('analytics.types.attitude'),
                    backgroundColor: 'rgba(53, 196, 220, 0.2)',
                    borderColor: 'rgb(53, 196, 220)',
                    pointBackgroundColor: 'rgba(255,99,132,1)',
                    pointHoverBorderColor: 'rgba(255,99,132,1)',
                    data: data?.sales_strengths.attitude
                }
            ]
        })

    }, [data?.sales_strengths, copy?.salesStrengthsLabels])

    useEffect(() => {

        setTeamsChartData({
            labels: copy?.teamStrengthsLabels,
            datasets: [
                {
                    label: t('analytics.tabs.teamStrengths'),
                    backgroundColor: 'rgba(255,99,132,0.2)',
                    borderColor: 'rgba(255,99,132,1)',
                    pointBackgroundColor: 'rgba(255,99,132,1)',
                    pointBorderColor: '#fff',
                    pointHoverBackgroundColor: '#fff',
                    pointHoverBorderColor: 'rgba(255,99,132,1)',
                    data: data?.team_strengths
                }
            ]
        })

    }, [data?.team_strengths, copy?.teamStrengthsLabels])

    useEffect(() => {

        setAdeptAspectsData({
            labels: copy?.adeptAspectsLabels,
            datasets: [
                {
                    label: `${t('analytics.tabs.adept')} Aspects`,
                    backgroundColor: 'rgba(255,99,132,0.2)',
                    borderColor: 'rgba(255,99,132,1)',
                    pointBackgroundColor: 'rgba(255,99,132,1)',
                    pointBorderColor: '#fff',
                    pointHoverBackgroundColor: '#fff',
                    pointHoverBorderColor: 'rgba(255,99,132,1)',
                    data: data?.adept_aspects_points
                }
            ]
        })

        setAdeptWorkStylesData({
            labels: copy?.adeptWorkStyles,
            datasets: [
                {
                    label: `${t('analytics.tabs.adept')} Work Styles`,
                    backgroundColor: 'rgba(255,99,132,0.2)',
                    borderColor: 'rgba(255,99,132,1)',
                    pointBackgroundColor: 'rgba(255,99,132,1)',
                    pointBorderColor: '#fff',
                    pointHoverBackgroundColor: '#fff',
                    pointHoverBorderColor: 'rgba(255,99,132,1)',
                    data: calculateWorkStyles()
                }
            ]
        })

    }, [data?.adept_aspects_points, copy?.adeptAspectsLabels, calculateWorkStyles, copy?.adeptWorkStyles])


    const [salesChartData, setSalesChartData] = useState({
        labels: copy?.salesStrengthsLabels,
        datasets: [
            {
                label: t('analytics.types.competence'),
                backgroundColor: 'rgba(179,181,198,0.2)',
                borderColor: 'rgba(179,181,198,1)',
                pointBackgroundColor: 'rgba(179,181,198,1)',
                pointHoverBorderColor: 'rgba(179,181,198,1)',
                data: data?.sales_strengths?.competence
            },
            {
                label: t('analytics.types.attitude'),
                backgroundColor: 'rgba(255,99,132,0.2)',
                borderColor: 'rgba(255,99,132,1)',
                pointBackgroundColor: 'rgba(255,99,132,1)',
                pointHoverBorderColor: 'rgba(255,99,132,1)',
                data: data?.sales_strengths?.attitude
            }
        ]
    });

    const [teamsChartData, setTeamsChartData] = useState({
        labels: copy?.teamStrengthsLabels,
        datasets: [
            {
                    label: t('analytics.tabs.teamStrengths'),
                backgroundColor: 'rgba(255,99,132,0.2)',
                borderColor: 'rgba(255,99,132,1)',
                pointBackgroundColor: 'rgba(255,99,132,1)',
                pointBorderColor: '#fff',
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: 'rgba(255,99,132,1)',
                data: data?.team_strengths
            }
        ]
    });

    const [adeptAspectsData, setAdeptAspectsData] = useState({
        labels: copy?.adeptAspectsLabels,
        datasets: [
            {
                    label: t('analytics.tabs.teamStrengths'),
                backgroundColor: 'rgba(255,99,132,0.2)',
                borderColor: 'rgba(255,99,132,1)',
                pointBackgroundColor: 'rgba(255,99,132,1)',
                pointBorderColor: '#fff',
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: 'rgba(255,99,132,1)',
                data: data?.adept_aspects_points
            }
        ]
    });

    const [adeptWorkStylesData, setAdeptWorkStylesData] = useState({
        labels: copy?.adeptWorkStyles,
        datasets: [
            {
                label: `${t('analytics.tabs.adept')} Work Styles`,
                backgroundColor: 'rgba(255,99,132,0.2)',
                borderColor: 'rgba(255,99,132,1)',
                pointBackgroundColor: 'rgba(255,99,132,1)',
                pointBorderColor: '#fff',
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: 'rgba(255,99,132,1)',
                data: calculateWorkStyles()
            }
        ]
    })

    const [lightOptionsTeams] = useState({
        maintainAspectRatio: false,
        plugins: {
            legend: {
                labels: {
                    color: '#495057'
                },
                position: 'bottom'
            }
        },
        scales: {
            r: {
                min: 0,
                max: 9,
                ticks: {
                    stepSize: 1, // the number of step
                },
                pointLabels: {
                    fonfontSize: 14,
                    color: '#495057',
                    padding: 0,
                    margin: 0
                },
                grid: {
                    color: '#ebedef',
                },
                angleLines: {
                    color: '#ebedef'
                }
            }
        }
    });

    const [lightOptionsSales] = useState({
        maintainAspectRatio: false,
        plugins: {
            legend: {
                labels: {
                    color: '#495057'
                },
                position: 'bottom',
            }
        },
        scales: {
            r: {
                max: 5,
                min: 0,
                stepSize: 1,
                ticks: {
                    stepSize: 1, // the number of step
                },
                pointLabels: {
                    fonfontSize: 14,
                    color: '#495057',
                    padding: 0,
                    margin: 0
                },
                grid: {
                    color: '#ebedef',
                },
                angleLines: {
                    color: '#ebedef'
                }
            }
        }
    });

    return (
        <>
            {data &&
                <div className="match-section">
                    <ToastContainer containerId="talent-match-score"/>
                    {data?.target_position_competencies_distribution && data?.target_position_competencies_distribution.length > 0 &&
                        <div className="white-box">
                            <TargetPositionComposition data={data} matchPercentage={matchPercentage}/>
                        </div>}

                    {companyData?.country === Constants.COUNTRY_CODE_FINLAND && 
                        <div className="talent-details-strengths-charts">
                            <RadarChart
                                title={t('analytics.tabs.teamStrengths')}
                                data={teamsChartData}
                                options={lightOptionsTeams}
                            />
                            <RadarChart
                                title={t('analytics.tabs.salesStrengths')}
                                data={salesChartData}
                                options={lightOptionsSales}
                            />
                        </div>
                    }

                    {data?.adept_aspects_points?.length > 0 && 
                        <div className="talent-details-strengths-charts">
                            <RadarChart
                                title={`${t('analytics.tabs.adept')} Aspects`}
                                data={adeptAspectsData}
                                options={lightOptionsTeams}
                            />
                            <RadarChart
                                title={`${t('analytics.tabs.adept')} Work Styles`}
                                data={adeptWorkStylesData}
                                options={lightOptionsTeams}
                            />
                        </div>
                    }

                    <div className="competences-motivation-section">
                        {candidateData?.competence_points?.length > 0 && <CandidateCompetences data={data}/>}
                        {candidateData?.motivation_points?.length > 0 && <CandidateMotivationFactors data={data}/>}
                    </div>
                    <div className="flex-div">
                        {Object.keys(candidateData?.skill_assessment).length !== 0 && (
                            <div className="assessments-section" id="boxStyleAssessment">
                                <div className="title">
                                    <p className="m-0">
                                        {t('talents.sections.reasoningAndSkills')}
                                        <ExplanationTooltip>
                                            {copy?.reasoningAndSkillsHelpLink}
                                        </ExplanationTooltip>
                                    </p>
                                    <p className="xs grey">
                                        {t('common.verifiedByAon')}
                                    </p>
                                </div>
                                <div className="stats">
                                <Accordion>
                                    {Object.entries(candidateData?.skill_assessment).map(
                                    ([id, score], index) => {
                                        const reasoning_assessment_content = copy?.reasoningAndSkills.find(
                                            (item) => Number(item.id) === Number(id)
                                        );
                                        return (
                                        <Accordion.Item eventKey={id} key={id}>
                                            <Accordion.Header className="custom-accordion-header">
                                            <div className="accordion-header-wrapper" >
                                                <p className="mar-5 right s">
                                                {getAONAssessmentLabelById(parseInt(id))}
                                                </p>
                                                {index === 0 ? (
                                                <div>
                                                    <div className="scoreline-div">
                                                    <span className="grey-bar">{t('common.scale.low')}</span>
                                                    <span className="grey-bar">{t('common.scale.average')}</span>
                                                    <span className="grey-bar">{t('common.scale.high')}</span>
                                                    </div>
                                                    <ScoreLine
                                                    val={Math.round((score / 100) * 10)}
                                                    />
                                                </div>
                                                ) : (
                                                <ScoreLine
                                                    val={Math.round((score / 100) * 10)}
                                                />
                                                )}
                                            </div>
                                            </Accordion.Header>
                                            <Accordion.Body>
                                            {reasoning_assessment_content &&
                                                reasoning_assessment_content.description}
                                            </Accordion.Body>
                                        </Accordion.Item>
                                        );
                                    }
                                    )}
                                </Accordion>
                                </div>
                            </div>
                        )}
                    </div>
                    {data?.assessment_report_list?.length > 0 && <div className="" id="boxStyleReports">
                        <div className="title">
                            <p className='m-0'>{copy?.downloadableReportsSectionTitle}</p>
                        </div>
                        <div >
                            {data?.assessment_report_list?.map((assessment_report, index) => (
                                copy?.assessmentReportNames[assessment_report.assessment_name] ? 
                                <AssessmentReport 
                                    talentID={data?.info?.id} 
                                    reportName={copy?.assessmentReportNames[assessment_report.assessment_name]}
                                    assessmentReport={assessment_report} 
                                    reportLanguages={copy?.reportLanguages[assessment_report.report_id]?.languages || Constants.languageSelectOptions}
                                    index={index} 
                                    toast={toast}
                                />
                                : null
                            ))}
                        </div>
                    </div>}
                </div>
            }
        </>
    )
}

const AssessmentReport = ({ talentID, assessmentReport, reportName, reportLanguages, index, toast}) => {
    const [language, setLanguage] = useState(2); // Default language is English (id = 2)
    const [downloadLink, setDownloadLink] = useState(null);
    const [generateReportVisible, setGenerateReportVisible] = useState(true);

    const b2bServiceObj = useMemo(() => {
        return new B2BApiService();
    }, []);

    const handleSelectLanguageChange = (e) => {
        setLanguage(e.target.value);
        setGenerateReportVisible(true);
        setDownloadLink(null);
    };

    const getDownloadLink = async () => {
        const toastId = toast.loading(t('talents.toasts.generatingReport'), {containerId: "talent-match-score"});
        try {
            const response = await b2bServiceObj.LoadAssessmentReport({
                talent_id: talentID,
                assessment_id: assessmentReport.instrument_id,
                report_id: assessmentReport.report_id,
                language_id: language
            });
            if (response?.httpStatus === 200 && response?.json?.data) {
                setDownloadLink(response.json.data.download_link);
                setGenerateReportVisible(false);
                toast.update(toastId, {
                    render: t('talents.toasts.reportGenerated'),
                    type: "success",
                    isLoading: false,
                    autoClose: 3000,
                    containerId: "talent-match-score"
                });
            } else if (response?.httpStatus === 404 && response?.json?.error === "No report found") {
                toast.update(toastId, {
                    render: reportName + " is not available in " + reportLanguages.find(lang => lang.code.toString() === language.toString())?.title || "the selected language",
                    type: "error",
                    isLoading: false,
                    autoClose: 5000,
                    containerId: "talent-match-score"
                });
                setDownloadLink(null);
            } else {
                toast.update(toastId, {
                    render: t('talents.toasts.reportFailedRetry'),
                    type: "error",
                    isLoading: false,
                    autoClose: 3000,
                    containerId: "talent-match-score"
                });
                setDownloadLink(null);
            }

        } catch (error) {
            console.error("Error fetching download link:", error);
            toast.update(toastId, {
                render: t('talents.toasts.reportFailed'),
                type: "error",
                isLoading: false,
                autoClose: 2000,
                containerId: "talent-match-score"
            });
            setDownloadLink(null);
        }
    };

    return (
        <div className="container" index={index}>
            <div className="image-container">
                <img src={downloadableReport} alt={t('talents.downloadableReportAlt')} className="downloadable-report-icon" />
                <p>{reportName}</p>
            </div>
            <select className="language-select" name="report-language-select"
                style={{ 
                    height: "40px",
                    padding: "5px 30px 5px 10px", // Add right padding to avoid overlapping the arrow
                    margin: "0 10px",
                    boxSizing: "border-box", // Ensures padding doesn't distort box dimensions
                    appearance: "none", // Optional: for full control over styling (may hide default arrow)
                    WebkitAppearance: "none", // Safari
                    MozAppearance: "none", // Firefox
                    background: `url('data:image/svg+xml;utf8,<svg fill="black" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M7 10l5 5 5-5z"/></svg>') no-repeat right 10px center`,
                    backgroundColor: "#fff",
                    backgroundSize: "32px",
                }}
                value={language} 
                onChange={handleSelectLanguageChange}
            >
                {reportLanguages.map((option, index) => (
                    <option key={index} value={option.code}>
                        {option.title}
                    </option>
                ))}
            </select>
            {generateReportVisible && (<button className="mazhrBtn default-green m-10 right" onClick={getDownloadLink}>
                {t('analytics.generateReport')}
            </button>)}
            {downloadLink && (
                <a className="mazhrBtn default-green" href={downloadLink} target="_blank" rel="noreferrer">
                    Download in {reportLanguages.find(lang => lang.code.toString() === language.toString())?.title || "the selected language"}
                </a>
            )}
        </div>
    );
}

export default TalentMatchScore;





